/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import { ROUTES } from "~/routing/routes";
import { Actions } from "~/views/plans/PlanDetails/components/Actions";
import {
  FlexColumnMax,
  MediumColumn,
  MediumIconColumn,
  SmallIconColumn,
} from "../../../components/Tables/styles";
import { NextDateCell } from "./NextDateCell";
import { PlanStateIconCell } from "./PlanStateIconCell";

export type Plan = {
  id: string;
  name: string;
  scheduleCron: string;
  scheduleExecutionDates: string[];
  scheduleTimezone: string;
  enabled: boolean;
  nextExecutionDates: string[];
  scheduleOffset: number;
};

const columnHelper = createColumnHelper<Plan>();

export function columns() {
  return [
    columnHelper.accessor(() => null, {
      id: "color",
      header: () => null,
      cell: ({ row }) => {
        return (
          <PlanStateIconCell
            enabled={row.original.enabled}
            cronExpression={row.original.scheduleCron}
            scheduleExecutionDates={row.original.scheduleExecutionDates}
          />
        );
      },
      meta: MediumIconColumn,
    }),
    columnHelper.accessor((row) => row?.name, {
      header: "Plan Name",
      cell: ({ row }) => (
        <NameWithLink
          name={row.original.name}
          link={ROUTES.plans.generateDetails(row.original.id)}
          containerStyle={{ wordBreak: "break-word", minWidth: "10rem" }}
        />
      ),
      meta: FlexColumnMax,
    }),
    columnHelper.accessor((row) => row?.name, {
      header: "Next Execution (local time)",
      cell: ({ row }) => {
        return NextDateCell({
          cronExpression: row.original.scheduleCron,
          timezone:
            row.original.scheduleTimezone &&
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          scheduleExecutionDates: row.original.scheduleExecutionDates,
          nextExecutionDates: row.original.nextExecutionDates,
          scheduleOffset: row.original.scheduleOffset,
        });
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor((row) => row?.name, {
      header: "Next Execution (plan time)",
      cell: ({ row }) => {
        return NextDateCell({
          cronExpression: row.original.scheduleCron,
          timezone: row.original.scheduleTimezone,
          scheduleExecutionDates: row.original.scheduleExecutionDates,
          nextExecutionDates: row.original.nextExecutionDates,
          scheduleOffset: row.original.scheduleOffset,
        });
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor((row) => row?.name, {
      header: "",
      id: "actions",
      cell: ({ row }) => (
        <Actions
          planId={row.original.id}
          planName={row.original.name}
          isPlanActive={row.original.enabled}
        />
      ),
      meta: SmallIconColumn,
    }),
  ] as never;
}
